import * as React from 'react';
import type { MappingRecord, TenantFeatureMode } from '../../domain/models.js';
import { repository } from '../../repositories/index.js';

const stateLabels: Record<MappingRecord['state'], string> = {
  suggested: 'Предложено',
  confirmed: 'Подтверждено',
  rejected: 'Отклонено',
  issue: 'Проблема сопоставления',
};

function MappingDetail({ record, onClose, readOnly }: { record: MappingRecord; onClose: () => void; readOnly: boolean }) {
  const [reported, setReported] = React.useState(false);
  return (
    <aside className="detail-drawer" aria-label="Детали сопоставления" data-testid="mapping-detail" data-read-only={readOnly ? 'true' : 'false'}>
      <div className="drawer-head">
        <div><span className="eyebrow">СОПОСТАВЛЕНИЕ</span><h2>{record.supplierArticle}</h2></div>
        <button type="button" className="icon-button close-text" onClick={onClose} aria-label="Закрыть">×</button>
      </div>
      <div className="mapping-detail-state-line">
        <span className={`mapping-state mapping-${record.state}`}>{stateLabels[record.state]}</span>
        {readOnly ? <span className="readonly-chip">Только чтение</span> : null}
      </div>
      {record.issueState ? <span className="issue-state">Статус проблемы: {record.issueState}</span> : null}
      <dl className="definition-list drawer-definition">
        <div><dt>Поставщик</dt><dd>{record.supplier}</dd></div>
        <div><dt>Товар поставщика</dt><dd>{record.supplierProduct}</dd></div>
        <div><dt>Артикул поставщика</dt><dd>{record.supplierArticle}</dd></div>
        <div><dt>Предложенный товар 1С</dt><dd>{record.proposedCanonical}</dd></div>
        <div><dt>Канонический артикул</dt><dd>{record.canonicalArticle}</dd></div>
      </dl>
      <section className="evidence-box">
        <h3>Основания</h3>
        <ul>{record.evidence.map((entry) => <li key={entry}>{entry}</li>)}</ul>
      </section>
      {readOnly ? (
        <p className="boundary-note readonly-boundary" data-testid="support-readonly-note">Support Mode показывает сопоставление только для чтения. Изменяющие действия недоступны.</p>
      ) : (
        <>
          <p className="boundary-note">Действия ниже изменяют только демонстрационное состояние интерфейса. Реальные записи сопоставлений не подключены.</p>
          <div className="button-row">
            <button type="button" className="button primary" data-mutation-control="confirm-mapping">Подтвердить</button>
            <button type="button" className="button secondary" data-mutation-control="reject-mapping">Отклонить</button>
          </div>
          <button type="button" className="button text-button" data-mutation-control="report-mapping" onClick={() => setReported(true)}>Сообщить о проблеме</button>
          {reported ? <div className="inline-success" role="status">Проблема зарегистрирована в mock-режиме со статусом open.</div> : null}
        </>
      )}
    </aside>
  );
}

interface MappingsPageProps {
  mode?: TenantFeatureMode;
}

export function MappingsPage({ mode = 'tenant' }: MappingsPageProps) {
  const readOnly = mode === 'support-readonly';
  const [records, setRecords] = React.useState<MappingRecord[]>([]);
  const [selected, setSelected] = React.useState<MappingRecord | null>(null);
  const [filter, setFilter] = React.useState<'all' | MappingRecord['state']>('all');

  React.useEffect(() => {
    repository.listMappings().then(setRecords);
  }, []);

  const shown = filter === 'all' ? records : records.filter((record) => record.state === filter);
  return (
    <div className="page mappings-page" data-testid="mappings-page" data-read-only={readOnly ? 'true' : 'false'}>
      <header className="page-header with-actions">
        <div><p className="eyebrow">ТЕНАНТ · КАЧЕСТВО ДАННЫХ</p><h1>Сопоставления</h1><p>Очередь проверки товаров поставщиков и их связи с каноническими товарами 1С.</p></div>
        <div className="header-action-group">
          {readOnly ? <span className="readonly-chip">Только чтение</span> : null}
          <button type="button" className="button secondary" onClick={() => setFilter('issue')}>Только проблемы</button>
        </div>
      </header>
      <div className="segmented-filter" aria-label="Фильтр состояния">
        {(['all', 'suggested', 'confirmed', 'rejected', 'issue'] as const).map((value) => (
          <button type="button" key={value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>
            {value === 'all' ? 'Все' : stateLabels[value]}
          </button>
        ))}
      </div>
      <section className="panel table-panel">
        <div className="data-table mapping-table" role="table">
          <div className="data-row data-header"><span>Поставщик</span><span>Товар поставщика</span><span>Артикул</span><span>Кандидат 1С</span><span>Состояние</span><span /></div>
          {shown.map((record) => (
            <div className="data-row" key={record.id}>
              <strong>{record.supplier}</strong><span>{record.supplierProduct}</span><span>{record.supplierArticle}</span><span>{record.proposedCanonical}</span>
              <span className={`mapping-state mapping-${record.state}`}>{stateLabels[record.state]}</span>
              <button type="button" className="button small secondary" onClick={() => setSelected(record)}>{readOnly ? 'Просмотреть' : 'Проверить'}</button>
            </div>
          ))}
        </div>
      </section>
      {selected ? <><button type="button" className="drawer-backdrop" aria-label="Закрыть детали" onClick={() => setSelected(null)} /><MappingDetail record={selected} readOnly={readOnly} onClose={() => setSelected(null)} /></> : null}
    </div>
  );
}
